import type { TableProps as AntdTableProps } from "antd";
import { Table as AntdTable } from "antd";
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from "antd/es/table/interface";
import { useCallback, useMemo, useState } from "react";

import { cn } from "@/lib/cn";

import { TABLE_MIN_SCROLL_Y } from "./constants";
import { useFillHeightScrollY } from "./hooks/useFillHeightScrollY";
import styles from "./Table.module.css";
import type { PlatformTableProps, TableSortState } from "./types";
import {
  hasSortableColumn,
  injectSortOrder,
  resolveSorterField,
  sortFlatData,
  sortTreeData,
} from "./utils";

export type { PlatformTableProps, TableSortState } from "./types";

/**
 * 平台 Table：透传 antd Table 全部 props 与样式，内置列头排序（扁平 / 树形）
 * 有 dataIndex 的列默认可排序；不需要排序的列设 sorter: false
 * fillHeight 默认 true：在 ListPageLayout 等 flex 容器内自动撑满并设置 scroll.y
 * 默认 scroll.x 为 max-content，表头不换行，列宽超出时出现横向滚动条
 */
export const Table = <T extends object>({
  columns,
  dataSource,
  treeSort = false,
  sortComparators,
  onChange,
  fillHeight = true,
  bottomReserve = 0,
  minScrollY = TABLE_MIN_SCROLL_Y,
  scroll,
  pagination,
  className,
  loading,
  ...rest
}: PlatformTableProps<T>) => {
  // 当前列排序状态，初始无序，用户点击列头后生效
  const [sortState, setSortState] = useState<TableSortState | null>(null);
  // 是否存在可排序列，无则完全透传 antd 行为
  const sortEnabled = useMemo(() => (columns ? hasSortableColumn(columns) : false), [columns]);
  // 是否展示分页（影响 fillHeight 底部预留）
  const showPagination = pagination !== false;
  // 本地排序后的 dataSource
  const sortedDataSource = useMemo(() => {
    if (!dataSource || !sortEnabled || !sortState) return dataSource;
    if (treeSort) {
      return sortTreeData(dataSource, sortState, sortComparators);
    }
    return sortFlatData(dataSource, sortState, sortComparators);
  }, [dataSource, sortComparators, sortEnabled, sortState, treeSort]);

  // 稳定 remeasureKey，避免 inline 数组引用每次 render 变化触发 useLayoutEffect 死循环
  const fillRemeasureKey = `${sortedDataSource?.length ?? 0}-${Boolean(loading)}-${columns?.length ?? 0}`;
  const { wrapperRef, scrollY } = useFillHeightScrollY({
    enabled: fillHeight,
    showPagination,
    bottomReserve,
    minScrollY,
    remeasureKey: fillRemeasureKey,
  });
  // 注入 sortOrder 后的列定义
  const mergedColumns = useMemo(() => {
    if (!columns || !sortEnabled) return columns;
    return injectSortOrder(columns, sortState);
  }, [columns, sortEnabled, sortState]);

  /** 合并外部 scroll：默认开启横向 max-content，表头不换行时可横向滚动 */
  const mergedScroll = useMemo(() => {
    const x = scroll?.x ?? "max-content";
    if (scroll?.y !== undefined) {
      return { ...scroll, x };
    }
    if (fillHeight && scrollY !== undefined) {
      return { ...scroll, x, y: scrollY };
    }
    return { ...scroll, x };
  }, [fillHeight, scroll, scrollY]);

  /** 合并列头排序与外部 onChange */
  const handleChange: AntdTableProps<T>["onChange"] = useCallback(
    (
      paginationConfig: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>,
    ) => {
      if (sortEnabled) {
        const next = resolveSorterField(sorter);
        setSortState(next ? { field: next.field, order: next.order! } : null);
      }
      onChange?.(paginationConfig, filters, sorter, extra);
    },
    [onChange, sortEnabled],
  );

  const tableNode = (
    <AntdTable<T>
      {...rest}
      className={cn(
        styles.platformTable,
        fillHeight && scrollY !== undefined && "h-full",
        className,
      )}
      columns={mergedColumns}
      dataSource={sortedDataSource}
      scroll={mergedScroll}
      pagination={pagination ? { showSizeChanger: true, ...pagination } : false}
      loading={loading}
      onChange={sortEnabled ? handleChange : onChange}
    />
  );

  if (!fillHeight) return tableNode;

  return (
    <div
      ref={wrapperRef}
      className={cn(styles.fillWrapper, scrollY !== undefined && styles.bodyScrollable)}
    >
      {tableNode}
    </div>
  );
};
